import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { db, collection, onSnapshot, updateDoc, doc, handleFirestoreError, OperationType, query, orderBy } from '../firebase';
import { UserProfile, OfficeLocation } from '../types';
import { useAuth } from '../App';
import { USER_ROLES, ROLE_LABELS } from '../constants';
import { 
  Users, 
  MapPin, 
  ShieldCheck, 
  Plus, 
  Search, 
  Edit2, 
  X, 
  Save, 
  Loader2, 
  Building2, 
  Map as MapIcon,
  Trash2,
  ChevronRight,
  ChevronDown,
  Eye,
  AlertCircle,
  CheckCircle2
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { toast } from 'sonner';
import { addDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import { cn } from '../lib/utils';
import ConfirmationModal from '../components/ConfirmationModal';
import { EpamModule } from '../components/EpamModule';

const UserManagement: React.FC = () => {
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [locations, setLocations] = useState<OfficeLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'epam' || searchParams.get('tab') === 'epm' ? 'epam' : 'users';
  const [activeTab, setActiveTab] = useState<'users' | 'locations' | 'epam'>(initialTab);
  const { isAdmin } = useAuth();

  if (!isAdmin) {
    return <div className="p-10 text-center font-bold text-slate-500">Access Denied: Admin only</div>;
  }

  // User Edit State
  const [editingUser, setEditingUser] = useState<UserProfile | null>(null);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);

  // Location Edit State
  const [editingLocation, setEditingLocation] = useState<Partial<OfficeLocation> | null>(null);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);

  // Location View State
  const [viewingLocation, setViewingLocation] = useState<OfficeLocation | null>(null);
  const [isViewLocationModalOpen, setIsViewLocationModalOpen] = useState(false);

  useEffect(() => {
    const unsubscribeUsers = onSnapshot(collection(db, 'users'), (snapshot) => {
      setUsers(snapshot.docs.map(doc => ({ uid: doc.id, ...doc.data() } as UserProfile)));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'users');
    });

    const unsubscribeLocations = onSnapshot(query(collection(db, 'office_locations'), orderBy('createdAt', 'desc')), (snapshot) => {
      setLocations(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as OfficeLocation)));
      setLoading(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'office_locations');
      setLoading(false);
    });

    return () => {
      unsubscribeUsers();
      unsubscribeLocations();
    };
  }, []);

  const handleToggleStatus = async (userId: string, currentStatus: boolean | undefined) => {
    const newStatus = currentStatus === false ? true : false;
    try {
      await updateDoc(doc(db, 'users', userId), { isActive: newStatus });
      toast.success(`User ${newStatus ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `users/${userId}`);
    }
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;
    try {
      await updateDoc(doc(db, 'users', editingUser.uid), {
        isHOD: editingUser.isHOD || false,
        hodDepartments: editingUser.hodDepartments || [],
        department: editingUser.department || '',
        role: editingUser.role,
        updatedAt: Timestamp.now()
      });
      toast.success('User updated successfully');
      setIsUserModalOpen(false);
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, 'users');
    }
  };

  const handleSaveLocation = async () => {
    if (!editingLocation?.name) return;
    try {
      if (editingLocation.id) {
        await updateDoc(doc(db, 'office_locations', editingLocation.id), {
          ...editingLocation,
          updatedAt: Timestamp.now()
        });
        toast.success('Location updated');
      } else {
        await addDoc(collection(db, 'office_locations'), {
          ...editingLocation,
          createdAt: Timestamp.now(),
          branches: editingLocation.branches || []
        });
        toast.success('Location added');
      }
      setIsLocationModalOpen(false);
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'office_locations');
    }
  };

  const handleDeleteLocation = (id: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Location',
      message: 'Are you sure you want to delete this location?',
      onConfirm: async () => {
        try {
          await deleteDoc(doc(db, 'office_locations', id));
          toast.success('Location deleted');
        } catch (error) {
          handleFirestoreError(error, OperationType.DELETE, 'office_locations');
        }
      }
    });
  };

  if (!isAdmin) return <div className="p-8 text-center">Access Denied</div>;

  return (
    <div className="max-w-6xl mx-auto space-y-8 pb-20">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">User & Office Management</h1>
          <p className="text-slate-500 font-medium">Manage personnel, HOD designations, and office locations</p>
        </div>
        <div className="flex bg-slate-100 p-1 rounded-2xl flex-wrap gap-1">
          <button
            onClick={() => setActiveTab('users')}
            className={`px-6 py-2 rounded-xl font-bold transition-all ${activeTab === 'users' ? 'bg-white text-blue-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            Users
          </button>
          <button
            onClick={() => setActiveTab('epam')}
            className={`px-6 py-2 rounded-xl font-bold transition-all ${activeTab === 'epam' ? 'bg-white text-blue-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            EPM (PIN Reset)
          </button>
          <button
            onClick={() => setActiveTab('locations')}
            className={`px-6 py-2 rounded-xl font-bold transition-all ${activeTab === 'locations' ? 'bg-white text-blue-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            Office Locations
          </button>
        </div>
      </div>

      {activeTab === 'users' ? (
        <div className="space-y-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search users by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-4 bg-white border border-slate-100 rounded-2xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-medium"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {users.filter(u => 
              u.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) || 
              u.email.toLowerCase().includes(searchTerm.toLowerCase())
            ).map(user => (
              <motion.div
                key={user.uid}
                layout
                className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm hover:shadow-md transition-all group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center overflow-hidden">
                      {user.photoURL ? (
                        <img src={user.photoURL} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-blue-600 font-bold text-xl">{user.displayName?.[0] || user.email[0].toUpperCase()}</span>
                      )}
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 truncate max-w-[150px]">{user.displayName || 'Unnamed User'}</h3>
                      <p className="text-xs text-slate-500 truncate max-w-[150px]">{user.email}</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => {
                      setEditingUser(user);
                      setIsUserModalOpen(true);
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-xl font-bold text-xs hover:bg-blue-100 transition-all shadow-sm"
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                    Edit
                  </button>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Role</span>
                    <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-lg font-bold text-[10px] uppercase tracking-wider">
                      {ROLE_LABELS[user.role as keyof typeof ROLE_LABELS] || user.role}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Department</span>
                    <span className="font-bold text-slate-700">{user.department || 'N/A'}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Status</span>
                    <span className={cn(
                      "px-2 py-1 rounded-lg font-bold text-[10px] uppercase tracking-wider inline-flex items-center gap-1",
                      user.isActive === false ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-600"
                    )}>
                      {user.isActive === false ? 'Deactivated' : 'Active'}
                    </span>
                  </div>
                  <div className="pt-2 flex items-center justify-end gap-2">
                    <button
                      onClick={() => handleToggleStatus(user.uid, user.isActive)}
                      className={cn(
                        "p-2 rounded-xl transition-all font-bold text-[10px] uppercase tracking-widest flex items-center gap-2",
                        user.isActive === false 
                          ? "bg-emerald-50 text-emerald-600 hover:bg-emerald-100" 
                          : "bg-red-50 text-red-600 hover:bg-red-100"
                      )}
                    >
                      {user.isActive === false ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
                      {user.isActive === false ? 'Activate' : 'Deactivate'}
                    </button>
                  </div>
                  {user.isHOD && (
                    <div className="pt-2 border-t border-slate-50">
                      <div className="flex items-center gap-2 text-orange-600 mb-1">
                        <ShieldCheck className="w-4 h-4" />
                        <span className="text-[10px] font-black uppercase tracking-widest">Head of Department</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {user.hodDepartments?.map(dept => (
                          <span key={dept} className="px-2 py-0.5 bg-orange-50 text-orange-700 rounded-md text-[10px] font-bold">
                            {dept}
                          </span>
                        )) || <span className="text-[10px] text-slate-400 italic">No departments assigned</span>}
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      ) : activeTab === 'epam' ? (
        <EpamModule />
      ) : (
        <div className="space-y-6">
          <div className="flex justify-end">
            <button
              onClick={() => {
                setEditingLocation({ name: '', address: '', branches: [] });
                setIsLocationModalOpen(true);
              }}
              className="flex items-center gap-2 bg-blue-800 text-white px-6 py-3 rounded-2xl font-bold hover:bg-blue-900 transition-all shadow-lg shadow-blue-200"
            >
              <Plus className="w-5 h-5" />
              Add Office Location
            </button>
          </div>

          <div className="grid grid-cols-1 gap-6">
            {locations.map(loc => (
              <div key={loc.id} className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-emerald-50 rounded-2xl flex items-center justify-center text-emerald-600">
                      <Building2 className="w-7 h-7" />
                    </div>
                    <div>
                      <h3 className="text-xl font-black text-slate-900">{loc.name}</h3>
                      <p className="text-slate-500 flex items-center gap-1 text-sm">
                        <MapPin className="w-3 h-3" />
                        {loc.address}
                      </p>
                      {loc.latitude && loc.longitude && (
                        <p className="text-[10px] text-slate-400 font-mono mt-1">
                          LAT: {loc.latitude} | LNG: {loc.longitude}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={() => {
                        setViewingLocation(loc);
                        setIsViewLocationModalOpen(true);
                      }}
                      className="flex items-center justify-center gap-2 px-4 py-2 bg-slate-100 text-slate-600 rounded-xl text-xs font-bold hover:bg-slate-200 transition-all"
                    >
                      <Eye className="w-4 h-4" />
                      View Information
                    </button>
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => {
                          setEditingLocation(loc);
                          setIsLocationModalOpen(true);
                        }}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-xl text-xs font-bold hover:bg-blue-100 transition-all"
                      >
                        <Edit2 className="w-4 h-4" />
                        Manage Data
                      </button>
                      <button 
                        onClick={() => handleDeleteLocation(loc.id)}
                        className="p-2 hover:bg-red-50 rounded-xl text-slate-400 hover:text-red-600 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest">Branches ({loc.branches?.length || 0})</h4>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {loc.branches?.map((branch, idx) => (
                      <div key={idx} className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                        <p className="font-bold text-slate-900 text-sm">{branch.name}</p>
                        <p className="text-xs text-slate-500 mt-1">{branch.address}</p>
                        {branch.latitude && branch.longitude && (
                          <div className="mt-2 flex items-center gap-1 text-[9px] text-slate-400 font-mono">
                            <MapIcon className="w-3 h-3" />
                            {branch.latitude}, {branch.longitude}
                          </div>
                        )}
                      </div>
                    ))}
                    {(!loc.branches || loc.branches.length === 0) && (
                      <p className="text-sm text-slate-400 italic">No branches added to this location</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* User Edit Modal */}
      <AnimatePresence>
        {isUserModalOpen && editingUser && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white w-full max-w-lg rounded-[32px] shadow-2xl overflow-hidden"
            >
              <div className="p-8 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                <div>
                  <h2 className="text-2xl font-black text-slate-900 tracking-tight">Edit User Permissions</h2>
                  <p className="text-slate-500 text-sm font-medium">{editingUser.displayName || editingUser.email}</p>
                </div>
                <button onClick={() => setIsUserModalOpen(false)} className="p-2 hover:bg-white rounded-xl shadow-sm transition-all">
                  <X className="w-6 h-6 text-slate-400" />
                </button>
              </div>

              <div className="p-8 space-y-6 max-h-[60vh] overflow-y-auto">
                <div className="space-y-2">
                  <label className="text-xs font-black text-slate-400 uppercase tracking-widest">System Role</label>
                  <select
                    value={editingUser.role}
                    onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value as any })}
                    className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                  >
                    {Object.entries(ROLE_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-black text-slate-400 uppercase tracking-widest">Primary Department</label>
                  <select
                    value={editingUser.department || ''}
                    onChange={(e) => setEditingUser({ ...editingUser, department: e.target.value })}
                    className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                  >
                    <option value="">Select Department</option>
                    {['Technical', 'Finance', 'Operations', 'Sales', 'IT', 'HR', 'Logistics', 'Billing', 'Management', 'Customer Care'].map(dept => (
                      <option key={dept} value={dept}>{dept}</option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center justify-between p-4 bg-orange-50 rounded-2xl border border-orange-100">
                  <div className="flex items-center gap-3">
                    <ShieldCheck className="w-6 h-6 text-orange-600" />
                    <div>
                      <p className="font-bold text-orange-900">Head of Department</p>
                      <p className="text-[10px] text-orange-700 font-bold uppercase tracking-widest">HOD Status</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setEditingUser({ ...editingUser, isHOD: !editingUser.isHOD })}
                    className={`w-14 h-8 rounded-full transition-all relative ${editingUser.isHOD ? 'bg-orange-500' : 'bg-slate-200'}`}
                  >
                    <div className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-all ${editingUser.isHOD ? 'left-7' : 'left-1'}`} />
                  </button>
                </div>

                {editingUser.isHOD && (
                  <div className="space-y-4 animate-in fade-in slide-in-from-top-2">
                    <label className="text-xs font-black text-slate-400 uppercase tracking-widest">HOD Departments</label>
                    <div className="flex flex-wrap gap-2">
                      {['Technical', 'Finance', 'Operations', 'Sales', 'IT', 'HR', 'Logistics', 'Customer Care'].map(dept => (
                        <button
                          key={dept}
                          onClick={() => {
                            const current = editingUser.hodDepartments || [];
                            const next = current.includes(dept) 
                              ? current.filter(d => d !== dept)
                              : [...current, dept];
                            setEditingUser({ ...editingUser, hodDepartments: next });
                          }}
                          className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                            editingUser.hodDepartments?.includes(dept)
                              ? 'bg-orange-500 text-white shadow-md shadow-orange-500/20'
                              : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                          }`}
                        >
                          {dept}
                        </button>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Add custom department..."
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const val = (e.target as HTMLInputElement).value.trim();
                            if (val && !editingUser.hodDepartments?.includes(val)) {
                              setEditingUser({ 
                                ...editingUser, 
                                hodDepartments: [...(editingUser.hodDepartments || []), val] 
                              });
                              (e.target as HTMLInputElement).value = '';
                            }
                          }
                        }}
                        className="flex-1 px-4 py-2 bg-slate-50 border border-slate-100 rounded-xl text-xs font-bold focus:outline-none"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="p-8 bg-slate-50/50 border-t border-slate-100 flex gap-4">
                <button
                  onClick={() => setIsUserModalOpen(false)}
                  className="flex-1 px-6 py-4 bg-white border border-slate-200 text-slate-600 rounded-2xl font-bold hover:bg-slate-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateUser}
                  className="flex-1 px-6 py-4 bg-blue-800 text-white rounded-2xl font-bold hover:bg-blue-900 transition-all shadow-lg shadow-blue-200"
                >
                  Save Changes
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Location Edit Modal */}
      <AnimatePresence>
        {isLocationModalOpen && editingLocation && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white w-full max-w-2xl rounded-[32px] shadow-2xl overflow-hidden"
            >
              <div className="p-8 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                <div>
                  <h2 className="text-2xl font-black text-slate-900 tracking-tight">
                    {editingLocation.id ? 'Edit Office Location' : 'Add Office Location'}
                  </h2>
                  <p className="text-slate-500 text-sm font-medium">Configure office address and branches</p>
                </div>
                <button onClick={() => setIsLocationModalOpen(false)} className="p-2 hover:bg-white rounded-xl shadow-sm transition-all">
                  <X className="w-6 h-6 text-slate-400" />
                </button>
              </div>

              <div className="p-8 space-y-6 max-h-[70vh] overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-xs font-black text-slate-400 uppercase tracking-widest">Office Name</label>
                    <input
                      type="text"
                      value={editingLocation.name || ''}
                      onChange={(e) => setEditingLocation({ ...editingLocation, name: e.target.value })}
                      placeholder="e.g. Dar es Salaam HQ"
                      className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-black text-slate-400 uppercase tracking-widest">Physical Address</label>
                    <input
                      type="text"
                      value={editingLocation.address || ''}
                      onChange={(e) => setEditingLocation({ ...editingLocation, address: e.target.value })}
                      placeholder="Street, Building, Floor"
                      className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-xs font-black text-slate-400 uppercase tracking-widest">Latitude</label>
                    <input
                      type="number"
                      value={editingLocation.latitude || ''}
                      onChange={(e) => setEditingLocation({ ...editingLocation, latitude: parseFloat(e.target.value) })}
                      placeholder="-6.7924"
                      step="any"
                      className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-black text-slate-400 uppercase tracking-widest">Longitude</label>
                    <input
                      type="number"
                      value={editingLocation.longitude || ''}
                      onChange={(e) => setEditingLocation({ ...editingLocation, longitude: parseFloat(e.target.value) })}
                      placeholder="39.2083"
                      step="any"
                      className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                    />
                  </div>
                </div>

                <div className="pt-6 border-t border-slate-100">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-black text-slate-900">Branches</h3>
                    <button
                      onClick={() => {
                        const branches = editingLocation.branches || [];
                        setEditingLocation({
                          ...editingLocation,
                          branches: [...branches, { id: Math.random().toString(36).substr(2, 9), name: '', address: '' }]
                        });
                      }}
                      className="flex items-center gap-1 text-blue-600 font-bold text-sm hover:underline"
                    >
                      <Plus className="w-4 h-4" />
                      Add Branch
                    </button>
                  </div>

                  <div className="space-y-4">
                    {editingLocation.branches?.map((branch, idx) => (
                      <div key={idx} className="p-6 bg-slate-50 rounded-3xl border border-slate-100 space-y-4 relative group">
                        <button
                          onClick={() => {
                            const branches = editingLocation.branches?.filter((_, i) => i !== idx);
                            setEditingLocation({ ...editingLocation, branches });
                          }}
                          className="absolute top-4 right-4 p-2 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-1">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Branch Name</label>
                            <input
                              type="text"
                              value={branch.name}
                              onChange={(e) => {
                                const branches = [...(editingLocation.branches || [])];
                                branches[idx].name = e.target.value;
                                setEditingLocation({ ...editingLocation, branches });
                              }}
                              className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-bold"
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Branch Address</label>
                            <input
                              type="text"
                              value={branch.address}
                              onChange={(e) => {
                                const branches = [...(editingLocation.branches || [])];
                                branches[idx].address = e.target.value;
                                setEditingLocation({ ...editingLocation, branches });
                              }}
                              className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-bold"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-1">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Latitude</label>
                            <input
                              type="number"
                              value={branch.latitude || ''}
                              onChange={(e) => {
                                const branches = [...(editingLocation.branches || [])];
                                branches[idx].latitude = parseFloat(e.target.value);
                                setEditingLocation({ ...editingLocation, branches });
                              }}
                              step="any"
                              className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-bold"
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Longitude</label>
                            <input
                              type="number"
                              value={branch.longitude || ''}
                              onChange={(e) => {
                                const branches = [...(editingLocation.branches || [])];
                                branches[idx].longitude = parseFloat(e.target.value);
                                setEditingLocation({ ...editingLocation, branches });
                              }}
                              step="any"
                              className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-bold"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="p-8 bg-slate-50/50 border-t border-slate-100 flex gap-4">
                <button
                  onClick={() => setIsLocationModalOpen(false)}
                  className="flex-1 px-6 py-4 bg-white border border-slate-200 text-slate-600 rounded-2xl font-bold hover:bg-slate-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveLocation}
                  className="flex-1 px-6 py-4 bg-blue-800 text-white rounded-2xl font-bold hover:bg-blue-900 transition-all shadow-lg shadow-blue-200"
                >
                  {editingLocation.id ? 'Update Location' : 'Save Location'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Location View Modal */}
      <AnimatePresence>
        {isViewLocationModalOpen && viewingLocation && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white w-full max-w-2xl rounded-[32px] shadow-2xl overflow-hidden"
            >
              <div className="p-8 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600">
                    <Building2 className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-black text-slate-900 tracking-tight">{viewingLocation.name}</h2>
                    <p className="text-slate-500 text-sm font-medium">Location Details & Directions</p>
                  </div>
                </div>
                <button onClick={() => setIsViewLocationModalOpen(false)} className="p-2 hover:bg-white rounded-xl shadow-sm transition-all">
                  <X className="w-6 h-6 text-slate-400" />
                </button>
              </div>

              <div className="p-8 space-y-8 max-h-[70vh] overflow-y-auto">
                <div className="bg-slate-50 p-6 rounded-3xl border border-slate-100">
                  <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4">Main Office</h3>
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <p className="font-bold text-slate-900 text-lg">{viewingLocation.address}</p>
                      {viewingLocation.latitude && viewingLocation.longitude && (
                        <p className="text-xs text-slate-500 mt-1">Coordinates: {viewingLocation.latitude}, {viewingLocation.longitude}</p>
                      )}
                    </div>
                    {viewingLocation.latitude && viewingLocation.longitude && (
                      <a
                        href={`https://www.google.com/maps/dir/?api=1&destination=${viewingLocation.latitude},${viewingLocation.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 bg-blue-800 text-white px-6 py-3 rounded-2xl font-bold hover:bg-blue-900 transition-all shadow-lg shadow-blue-200 text-sm whitespace-nowrap"
                      >
                        <MapIcon className="w-4 h-4" />
                        Get Directions
                      </a>
                    )}
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest">Branches ({viewingLocation.branches?.length || 0})</h3>
                  <div className="grid grid-cols-1 gap-4">
                    {viewingLocation.branches?.map((branch, idx) => (
                      <div key={idx} className="p-6 bg-white border border-slate-100 rounded-3xl flex flex-col md:flex-row md:items-center justify-between gap-4 hover:shadow-md transition-all">
                        <div>
                          <p className="font-bold text-slate-900">{branch.name}</p>
                          <p className="text-sm text-slate-500">{branch.address}</p>
                        </div>
                        {branch.latitude && branch.longitude && (
                          <a
                            href={`https://www.google.com/maps/dir/?api=1&destination=${branch.latitude},${branch.longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 bg-slate-100 text-slate-600 px-4 py-2 rounded-xl font-bold hover:bg-slate-200 transition-all text-xs"
                          >
                            <MapIcon className="w-3 h-3" />
                            Directions
                          </a>
                        )}
                      </div>
                    ))}
                    {(!viewingLocation.branches || viewingLocation.branches.length === 0) && (
                      <p className="text-sm text-slate-400 italic text-center py-4">No branches registered for this location</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="p-8 bg-slate-50/50 border-t border-slate-100">
                <button
                  onClick={() => setIsViewLocationModalOpen(false)}
                  className="w-full px-6 py-4 bg-white border border-slate-200 text-slate-600 rounded-2xl font-bold hover:bg-slate-50 transition-all"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmLabel="YES"
        cancelLabel="NO"
      />
    </div>
  );
};

export default UserManagement;
